import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useRealtimeStore } from "../../lib/store.js";

// Ссылки в «О себе» делаем кликабельными: явные http(s)/www и частые
// «голые» домены мессенджеров (wa.me/…, t.me/…, snapchat.com/… и т.п.).
const LINK_PATTERN =
  /(https?:\/\/[^\s]+|www\.[^\s]+|(?:wa\.me|t\.me|snapchat\.com|instagram\.com|tiktok\.com)\/[^\s]+)/gi;

const linkifyBio = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(LINK_PATTERN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }
    const raw = match[0];
    const href = raw.startsWith("http") ? raw : `https://${raw}`;
    nodes.push(
      <a key={`${start}-${raw}`} href={href} target="_blank" rel="noreferrer">
        {raw}
      </a>
    );
    lastIndex = start + raw.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

// Ячейка в стиле Telegram Settings: маленький label сверху, значение снизу,
// действие (копирование) — trailing-иконка справа.
const ProfileCell = ({
  label,
  children,
  copyValue,
  copiedLabel,
  onCopied,
  bio = false
}: {
  label: string;
  children: ReactNode;
  copyValue?: string;
  copiedLabel?: string;
  onCopied?(message: string): void;
  bio?: boolean;
}) => (
  <div className={bio ? "profile-cell profile-cell--bio" : "profile-cell"}>
    <div className="profile-cell__body">
      <span className="profile-cell__label">{label}</span>
      <span className="profile-cell__value">{children}</span>
    </div>
    {copyValue ? (
      <button
        type="button"
        className="icon-button profile-cell__copy"
        onClick={() => {
          void navigator.clipboard?.writeText(copyValue);
          onCopied?.(copiedLabel ?? "Скопировано");
        }}
        aria-label={`Копировать: ${label}`}
        title="Копировать"
      >
        <i className="ic ic-copy" aria-hidden="true" />
      </button>
    ) : null}
  </div>
);

export const SelfProfilePanel = () => {
  const open = useRealtimeStore((state) => state.selfProfileOpen);
  const close = useRealtimeStore((state) => state.closeSelfProfile);
  const identity = useRealtimeStore((state) => state.selfIdentity);
  const requestIdentity = useRealtimeStore((state) => state.requestSelfIdentity);
  const isSupportRole = useRealtimeStore((state) => state.appRole === "support");
  const [copyToast, setCopyToast] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, open]);

  // При открытии панели — если identity ещё не получено, попросить worker эмитнуть.
  // Worker отвечает событием account.identity, store обновляется автоматически.
  useEffect(() => {
    if (open && !identity) {
      requestIdentity();
    }
  }, [open, identity, requestIdentity]);

  // Тост «✓ скопировано» гаснет сам.
  useEffect(() => {
    if (!copyToast) {
      return;
    }
    const handle = window.setTimeout(() => setCopyToast(null), 1600);
    return () => window.clearTimeout(handle);
  }, [copyToast]);

  if (!open) {
    return null;
  }

  const fullName =
    [identity?.firstName, identity?.lastName].filter(Boolean).join(" ").trim() || "Без имени";
  const showToast = (message: string): void => setCopyToast(message);

  return createPortal(
    <div
      className="self-profile__layer"
      role="dialog"
      aria-modal="true"
      aria-label="Мой профиль"
      onClick={close}
    >
      <div className="self-profile__panel" onClick={(event) => event.stopPropagation()}>
        <header className="self-profile__header">
          <strong>Мой профиль</strong>
          <div className="self-profile__header-actions">
            <button
              type="button"
              className="icon-button"
              onClick={() => requestIdentity()}
              aria-label="Обновить данные из Telegram"
              title="Обновить данные из Telegram"
            >
              <i className="ic ic-refresh" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={close}
              aria-label="Закрыть"
            >
              <i className="ic ic-close" aria-hidden="true" />
            </button>
          </div>
        </header>
        {!identity ? (
          <div className="self-profile__empty">
            Идентификация ещё не получена. Проверьте, что аккаунт авторизован — запрос
            отправлен автоматически.
          </div>
        ) : (
          <div className="self-profile__cells">
            <ProfileCell label="Имя">{fullName}</ProfileCell>

            <ProfileCell
              label="ID"
              copyValue={String(identity.telegramUserId)}
              copiedLabel="ID скопирован"
              onCopied={showToast}
            >
              {identity.telegramUserId}
            </ProfileCell>

            {!isSupportRole && identity.username ? (
              <ProfileCell
                label="Имя пользователя"
                copyValue={`https://t.me/${identity.username}`}
                copiedLabel="Ссылка скопирована"
                onCopied={showToast}
              >
                <a
                  href={`https://t.me/${identity.username}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  @{identity.username}
                </a>
              </ProfileCell>
            ) : null}

            {!isSupportRole && identity.phoneNumber ? (
              <ProfileCell
                label="Телефон"
                copyValue={`+${identity.phoneNumber}`}
                copiedLabel="Телефон скопирован"
                onCopied={showToast}
              >
                +{identity.phoneNumber}
              </ProfileCell>
            ) : null}

            {!isSupportRole && identity.bio ? (
              <ProfileCell
                label="О себе"
                bio
                copyValue={identity.bio}
                copiedLabel="Описание скопировано"
                onCopied={showToast}
              >
                {linkifyBio(identity.bio)}
              </ProfileCell>
            ) : null}

            {isSupportRole ? (
              <ProfileCell label="Режим">
                Саппорт — контактные данные скрыты по политике.
              </ProfileCell>
            ) : null}
          </div>
        )}
        {copyToast ? (
          <div className="copy-toast" role="status">
            <i className="ic ic-check" aria-hidden="true" /> {copyToast}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};
